"use client";

import type { ShopBranchAdmin } from "@/modules/merchant/presentation/api/client";
import { SHOP_CURRENCIES, ShopCurrencySchema } from "@/shared/lib/currency";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import {
  Button,
  Checkbox,
  FormInput,
  FormRoot,
  Label,
  Loader,
  Modal,
  RHF,
  zodResolver,
} from "@devhop/ui";
import { useEffect } from "react";
import { z } from "zod";
import { useCreateShopBranch, useUpdateShopBranch } from "../api/queries";

/** Radix Select ຫ້າມ value="" — ໃຊ້ sentinel ແລ້ວແປງເປັນ null ຕອນສົ່ງ API */
const BRANCH_CURRENCY_INHERIT = "__inherit__" as const;

const branchCurrencyChoice = z.union([
  z.literal(BRANCH_CURRENCY_INHERIT),
  ShopCurrencySchema,
]);

const CreateBranchSchema = z.object({
  name: z.string().min(1, "ຕ້ອງໃສ່ຊື່ສາຂາ"),
  phone: z.string().optional(),
  address: z.string().optional(),
  /** __inherit__ = ສືບທອດຈາກຮ້ານ */
  defaultCurrency: branchCurrencyChoice.default(BRANCH_CURRENCY_INHERIT),
});

const UpdateBranchSchema = CreateBranchSchema.extend({
  isActive: z.boolean(),
});

type CreateValues = z.infer<typeof CreateBranchSchema>;
type UpdateValues = z.infer<typeof UpdateBranchSchema>;

type ShopBranchFormModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  shopId: string;
  mode: "create" | "edit";
  branch?: ShopBranchAdmin | null;
  onSuccess?: () => void;
};

export function ShopBranchFormModal({
  open,
  onOpenChange,
  shopId,
  mode,
  branch,
  onSuccess,
}: ShopBranchFormModalProps) {
  const createMut = useCreateShopBranch(shopId);
  const updateMut = useUpdateShopBranch(shopId);

  const createForm = RHF.useForm<CreateValues>({
    resolver: zodResolver(CreateBranchSchema),
    defaultValues: {
      name: "",
      phone: "",
      address: "",
      defaultCurrency: BRANCH_CURRENCY_INHERIT,
    },
  });

  const updateForm = RHF.useForm<UpdateValues>({
    resolver: zodResolver(UpdateBranchSchema),
    defaultValues: {
      name: "",
      phone: "",
      address: "",
      defaultCurrency: BRANCH_CURRENCY_INHERIT,
      isActive: true,
    },
  });

  useEffect(() => {
    if (!open || !shopId) return;
    if (mode === "create") {
      createForm.reset({
        name: "",
        phone: "",
        address: "",
        defaultCurrency: BRANCH_CURRENCY_INHERIT,
      });
    } else if (mode === "edit" && branch) {
      updateForm.reset({
        name: branch.name,
        phone: branch.phone ?? "",
        address: branch.address ?? "",
        defaultCurrency: branch.defaultCurrency ?? BRANCH_CURRENCY_INHERIT,
        isActive: branch.isActive,
      });
    }
  }, [open, shopId, mode, branch?.id, createForm, updateForm, branch]);

  const isEdit = mode === "edit";
  const submitting = createMut.isPending || updateMut.isPending;

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={isEdit ? "ແກ້ໄຂສາຂາ" : "ເພີ່ມສາຂາ"}
      description={
        isEdit
          ? "ປ່ຽນຊື່ ທີ່ຢູ່ ຫຼືສະຖານະການໃຊ້ງານຂອງສາຂາ"
          : "ສ້າງສາຂາໃໝ່ໃນຮ້ານນີ້ (ໃຊ້ກັບສາງ ແລະ ມອບໝາຍພະນັກງານ)"
      }
      size="md"
    >
      {isEdit ? (
        <FormRoot<UpdateValues>
          methods={updateForm}
          onSubmit={async (vals) => {
            if (!branch) return;
            await updateMut.mutateAsync({
              branchId: branch.id,
              data: {
                name: vals.name.trim(),
                phone: vals.phone?.trim() || null,
                address: vals.address?.trim() || null,
                isActive: vals.isActive,
                defaultCurrency:
                  vals.defaultCurrency === BRANCH_CURRENCY_INHERIT
                    ? null
                    : vals.defaultCurrency,
              },
            });
            onOpenChange(false);
            onSuccess?.();
          }}
          className="space-y-4"
        >
          <FormInput
            name="name"
            label="ຊື່ສາຂາ"
            requiredMark
            placeholder="ສາຂາໃຈກາງ"
          />
          <FormInput name="phone" label="ໂທລະສັບ" placeholder="020..." />
          <FormInput name="address" label="ທີ່ຢູ່" placeholder="ບ້ານ / ເມືອງ" />
          <div className="space-y-2">
            <Label className="text-sm">ສະກຸນເລີ່ມຕົ້ນ (ສາຂາ)</Label>
            <RHF.Controller
              name="defaultCurrency"
              control={updateForm.control}
              render={({ field }) => (
                <SimpleSelect
                  value={field.value}
                  onValueChange={field.onChange}
                  options={[
                    {
                      value: BRANCH_CURRENCY_INHERIT,
                      label: "ສືບທອດຈາກຮ້ານ",
                    },
                    ...SHOP_CURRENCIES.map((c) => ({ value: c, label: c })),
                  ]}
                  placeholder="ເລືອກ"
                />
              )}
            />
            <p className="text-muted-foreground text-xs">
              ສືບທອດຈາກຮ້ານ — ໃຊ້ສະກຸນຕັ້ງຄ່າຮ້ານ (ແຜງຄວບຄຸມ)
            </p>
          </div>
          <div className="flex items-center gap-2">
            <RHF.Controller
              name="isActive"
              control={updateForm.control}
              render={({ field }) => (
                <>
                  <Checkbox
                    id="branch-isActive"
                    checked={field.value}
                    onCheckedChange={(c) => field.onChange(c === true)}
                  />
                  <Label
                    htmlFor="branch-isActive"
                    className="cursor-pointer text-sm"
                  >
                    ເປີດໃຊ້ງານ ສາຂາ
                  </Label>
                </>
              )}
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              ຍົກເລີກ
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? <Loader /> : null}
              <span className="ml-2">ບັນທຶກ</span>
            </Button>
          </div>
        </FormRoot>
      ) : (
        <FormRoot<CreateValues>
          methods={createForm}
          onSubmit={async (vals) => {
            await createMut.mutateAsync({
              name: vals.name.trim(),
              phone: vals.phone?.trim() || null,
              address: vals.address?.trim() || null,
              defaultCurrency:
                vals.defaultCurrency === BRANCH_CURRENCY_INHERIT
                  ? null
                  : vals.defaultCurrency,
            });
            onOpenChange(false);
            onSuccess?.();
          }}
          className="space-y-4"
        >
          <FormInput
            name="name"
            label="ຊື່ສາຂາ"
            requiredMark
            placeholder="ສາຂາໃຈກາງ"
          />
          <FormInput name="phone" label="ໂທລະສັບ" placeholder="020..." />
          <FormInput name="address" label="ທີ່ຢູ່" placeholder="ບ້ານ / ເມືອງ" />
          <div className="space-y-2">
            <Label className="text-sm">ສະກຸນເລີ່ມຕົ້ນ (ສາຂາ)</Label>
            <RHF.Controller
              name="defaultCurrency"
              control={createForm.control}
              render={({ field }) => (
                <SimpleSelect
                  value={field.value}
                  onValueChange={field.onChange}
                  options={[
                    {
                      value: BRANCH_CURRENCY_INHERIT,
                      label: "ສືບທອດຈາກຮ້ານ",
                    },
                    ...SHOP_CURRENCIES.map((c) => ({ value: c, label: c })),
                  ]}
                  placeholder="ເລືອກ"
                />
              )}
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              ຍົກເລີກ
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? <Loader /> : null}
              <span className="ml-2">ເພີ່ມສາຂາ</span>
            </Button>
          </div>
        </FormRoot>
      )}
    </Modal>
  );
}
